import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Modal, Select, Typography } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { userMessageForError } from '../../utils/apiError';
import { PRIORITIES, SEVERITIES, THIN_ROW_FIELDS, YES_NO, changedFields, clauseFieldFor, fieldLabel, isStaleEditConflict, staleEditMessage } from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

/**
 * Edit a pending draft row (PATCH) or revise a published one (POST .../revise).
 *
 *   mode 'edit'   : the row is an unpublished draft. If a human already approved it as an AI draft,
 *                   saving withdraws that approval (Commandment IV) - said BEFORE saving.
 *   mode 'revise' : the row is live in the current published version, which is immutable
 *                   (Commandment VII). Saving drafts the next version; a reason is required.
 *
 * Only changed fields are sent. The clause column differs per standard (clauseFieldFor).
 */
const EditQuestionModal = ({ open, mode, row, standardId, approvalWillReset = false, missingFields = [], unsafeField = null, onStale, onClose, onSaved }) => {
    const { request } = useApiRequest();
    const clauseField = clauseFieldFor(standardId);
    // The question number is editable on a pending draft only; a revision keeps the published number.
    const fields = useMemo(() => [...(mode === 'edit' ? ['questionNo'] : []), 'question', 'responseType', 'priority', ...THIN_ROW_FIELDS, ...(clauseField ? [clauseField] : [])], [clauseField, mode]);
    const [values, setValues] = useState({});
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && row) {
            const next = {};
            fields.forEach((f) => { next[f] = row[f] ?? ''; });
            setValues(next);
            setReason('');
            setError(null);
        }
    // Keyed on the row id, not the row object: a parent reload hands over a new object and must not wipe the
    // reviewer's typing or the error just shown.
    }, [open, row?.id, fields]);

    if (!row) return null;
    const isRevise = mode === 'revise';
    const delta = changedFields(row, values, fields);
    const questionEmpty = !String(values.question ?? '').trim();
    const canSave = Object.keys(delta).length > 0 && !questionEmpty && (!isRevise || !!reason.trim());

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const result = isRevise
                ? await request('POST', `/admin/question-bank/questions/${row.id}/revise`, { body: { reason: reason.trim(), ...delta } })
                : await request('PATCH', `/admin/question-bank/questions/${row.id}`, { body: delta });
            await onSaved(result.data);
        } catch (err) {
            if (isStaleEditConflict(err)) {
                setError(staleEditMessage());
                if (onStale) await onStale();
            } else {
                setError(userMessageForError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const set = (f) => (e) => setValues((v) => ({ ...v, [f]: e.target.value }));
    const setSelect = (f) => (v) => setValues((cur) => ({ ...cur, [f]: v || '' }));
    const requiredTag = (f) => (missingFields.includes(f) ? <Text type="danger"> (required to approve)</Text> : null);
    const unsafeTag = (f) => (unsafeField === f ? <Text type="danger" data-testid={`unsafe-${f}`}> (contains a link, email or markup; edit to plain text)</Text> : null);
    const fieldTags = (f) => <>{requiredTag(f)}{unsafeTag(f)}</>;

    return (
        <Modal
            open={open}
            title={isRevise ? `Revise published question ${row.questionNo}` : `Edit draft question ${row.questionNo}`}
            okText={isRevise ? 'Draft revision for next version' : 'Save changes'}
            onOk={save}
            onCancel={onClose}
            confirmLoading={busy}
            okButtonProps={{ disabled: !canSave }}
        >
            {isRevise ? (
                <Alert
                    type="info"
                    data-testid="revise-explainer"
                    message="This does not change the published question."
                    description="Saving creates a draft of the next version of this standard's question bank. The current published question stays live until you publish that version. Projects already pinned to the current version keep it unchanged; only projects that pin after the next version is published see this revision."
                    style={{ marginBottom: 12 }}
                />
            ) : approvalWillReset ? (
                <Alert
                    type="warning"
                    data-testid="approval-reset-warning"
                    message="Saving will withdraw your approval."
                    description="A human already approved this AI-drafted question. An edited question is a different artefact, so it goes back to awaiting review and must be approved again before it can be published."
                    style={{ marginBottom: 12 }}
                />
            ) : null}

            {!isRevise && (
                <div style={{ marginBottom: 12 }}>
                    <Text strong>{fieldLabel('questionNo')}</Text>{unsafeTag('questionNo')}
                    <Input aria-label="Question number" value={values.questionNo ?? ''} onChange={set('questionNo')} />
                </div>
            )}
            <Text strong>{fieldLabel('question')}</Text>{unsafeTag('question')}
            <Input.TextArea aria-label="Question text" rows={4} value={values.question ?? ''} onChange={set('question')} />
            {clauseField && (
                <div style={{ marginTop: 12 }}>
                    <Text strong>{fieldLabel(clauseField)}</Text>{unsafeTag(clauseField)}
                    <Input aria-label="Clause" value={values[clauseField] ?? ''} onChange={set(clauseField)} />
                </div>
            )}
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('priority')}</Text>
                <div>
                    <Select
                        aria-label="Priority"
                        style={{ width: 160 }}
                        value={values.priority || undefined}
                        placeholder="Priority"
                        onChange={(v) => setValues((cur) => ({ ...cur, priority: v || '' }))}
                        options={PRIORITIES.map((p) => ({ value: p, label: p }))}
                    />
                </div>
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('responseType')}</Text>
                <Input aria-label="Answer type" value={values.responseType ?? ''} onChange={set('responseType')} />
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('domain')}</Text>{fieldTags('domain')}
                <Input aria-label="Domain" placeholder="e.g. Organization, IT / IT enabled, People, Physical" value={values.domain ?? ''} onChange={set('domain')} />
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('severity')}</Text>{fieldTags('severity')}
                <div>
                    <Select
                        aria-label="Severity"
                        style={{ width: 160 }}
                        value={values.severity || undefined}
                        placeholder="Severity"
                        onChange={setSelect('severity')}
                        options={SEVERITIES.map((s) => ({ value: s, label: s }))}
                    />
                </div>
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('mandatory')}</Text>{fieldTags('mandatory')}
                <div>
                    <Select
                        aria-label="Mandatory"
                        style={{ width: 160 }}
                        value={values.mandatory || undefined}
                        placeholder="Y or N"
                        onChange={setSelect('mandatory')}
                        options={YES_NO.map((v) => ({ value: v, label: v }))}
                    />
                </div>
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('evidenceRequired')}</Text>{fieldTags('evidenceRequired')}
                <div>
                    <Select
                        aria-label="Evidence required"
                        style={{ width: 160 }}
                        value={values.evidenceRequired || undefined}
                        placeholder="Y or N"
                        onChange={setSelect('evidenceRequired')}
                        options={YES_NO.map((v) => ({ value: v, label: v }))}
                    />
                </div>
            </div>
            <div style={{ marginTop: 12 }}>
                <Text strong>{fieldLabel('expectedRespondent')}</Text>{fieldTags('expectedRespondent')}
                <Input aria-label="Expected respondent" placeholder="e.g. CISO, IT Head, HR Manager" value={values.expectedRespondent ?? ''} onChange={set('expectedRespondent')} />
            </div>
            {isRevise && (
                <div style={{ marginTop: 12 }}>
                    <Text strong>Reason for the revision (required)</Text>
                    <Input.TextArea aria-label="Reason for the revision" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
            )}
            {questionEmpty && <Text type="danger" style={{ display: 'block', marginTop: 8 }}>The question text cannot be empty.</Text>}
            {error && <Alert type="error" message={error} data-testid="edit-question-error" style={{ marginTop: 12 }} />}
        </Modal>
    );
};

export default EditQuestionModal;
